import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useTheme } from '../../context/ThemeContext';
import {
  Users,
  Search,
  Phone,
  MessageSquare,
  Calendar,
  Loader2,
  Play,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  UserPlus,
  Send,
  RefreshCw,
  Zap,
  Eye,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle2,
  Trash2,
  Square,
  CheckSquare,
  Download,
  Mail,
  Settings,
  Copy,
  Filter,
  X,
  DollarSign
} from 'lucide-react';

const SURGERY_OPTIONS = [
  { value: 'lasik',     label: 'LASIK' },
  { value: 'smile',     label: 'Smile' },
  { value: 'prk',       label: 'PRK' },
  { value: 'anillos',   label: 'Anillos' },
  { value: 'cataratas', label: 'Cataratas' },
  { value: 'otro',      label: 'Otro' },
];

const surgeryLabel = (val) => SURGERY_OPTIONS.find(o => o.value === val)?.label || val || '';
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 0 })}`;

const API = process.env.REACT_APP_BACKEND_URL;

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [stats, setStats] = useState(null);
  const [leadsStats, setLeadsStats] = useState(null);
  const [funnelStats, setFunnelStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leads');
  const [filter, setFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [funnelFilter, setFunnelFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showAddLead, setShowAddLead] = useState(false);
  const [newLead, setNewLead] = useState({ name: '', phone: '', source: 'manual', notes: '' });
  const [saving, setSaving] = useState(false);
  const [bulkStarting, setBulkStarting] = useState(false);
  const [expandedLead, setExpandedLead] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [outcomeOpenFor, setOutcomeOpenFor] = useState(null); // lead.id whose outcome panel is open
  const [outcomeDraft, setOutcomeDraft] = useState({}); // {attended, consultation_revenue, converted_to_surgery, surgery_type, surgery_revenue, notes}
  const [savingOutcome, setSavingOutcome] = useState(false);
  const [editingConvertedList, setEditingConvertedList] = useState([]); // Array para edición inline de consultas gestionadas
  const [addingNewPatient, setAddingNewPatient] = useState(false);
  const { theme } = useTheme();

  const cardBg = theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm';
  const inputBg = theme === 'dark' ? 'bg-slate-900 border-slate-600 text-white placeholder:text-gray-400' : 'bg-white border-gray-300 text-gray-900';
  const textPrimary = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const textSecondary = theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
  const textMuted = theme === 'dark' ? 'text-gray-500' : 'text-gray-500';

  // Backup config state
  const [backupConfig, setBackupConfig] = useState({ enabled: true, backup_phone: '' });
  const [showBackupConfig, setShowBackupConfig] = useState(false);
  const [savingBackup, setSavingBackup] = useState(false);

  useEffect(() => {
    loadData();
    loadBackupConfig();
  }, []);

  const loadBackupConfig = async () => {
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/clinic/${clinicId}/followup-backup-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBackupConfig(data);
      }
    } catch (err) {
      console.error('Error loading backup config:', err);
    }
  };

  const saveBackupConfig = async () => {
    setSavingBackup(true);
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API}/api/clinic/${clinicId}/followup-backup-config?enabled=${backupConfig.enabled}&backup_phone=${backupConfig.backup_phone}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (res.ok) {
        setShowBackupConfig(false);
      }
    } catch (err) {
      console.error('Error saving backup config:', err);
    }
    setSavingBackup(false);
  };

  const loadData = async () => {
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    
    if (!clinicId || !token) {
      console.error('Missing clinicId or token');
      setLoading(false);
      return;
    }
    
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [leadsRes, followUpsRes, statsRes, leadsStatsRes, funnelStatsRes] = await Promise.all([
        fetch(`${API}/api/clinic/${clinicId}/leads/with-funnel`, { headers }),
        fetch(`${API}/api/clinic/${clinicId}/follow-ups`, { headers }),
        fetch(`${API}/api/clinic/${clinicId}/follow-ups/stats`, { headers }),
        fetch(`${API}/api/clinic/${clinicId}/leads/stats`, { headers }),
        fetch(`${API}/api/clinic/${clinicId}/leads/funnel-stats`, { headers })
      ]);

      if (leadsRes.ok) {
        const leadsData = await leadsRes.json();
        setLeads(Array.isArray(leadsData) ? leadsData : []);
      }
      if (followUpsRes.ok) {
        const data = await followUpsRes.json();
        setFollowUps(data.leads || []);
      }
      if (statsRes.ok) setStats(await statsRes.json());
      if (leadsStatsRes.ok) setLeadsStats(await leadsStatsRes.json());
      if (funnelStatsRes.ok) setFunnelStats(await funnelStatsRes.json());
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const startFollowUp = async (lead) => {
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API}/api/clinic/${clinicId}/follow-ups`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lead_name: lead.name || 'Lead',
          lead_phone: lead.phone,
          lead_source: lead.source || 'whatsapp',
          notes: lead.ai_summary || ''
        })
      });
      
      if (response.ok) {
        loadData();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const toggleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const selectAllLeads = () => {
    // SOLO selecciona leads que NUNCA han tenido follow-up
    const leadsWithoutFollowUp = filteredLeads.filter(lead => {
      const phone_suffix = lead.phone?.slice(-10);
      if (!phone_suffix) return false;
      return !followUps.some(f => f.lead_phone?.includes(phone_suffix));
    });
    if (selectedLeads.length === leadsWithoutFollowUp.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leadsWithoutFollowUp.map(l => l.id));
    }
  };

  const deleteSelectedLeads = async () => {
    if (selectedLeads.length === 0) return;
    
    if (!window.confirm(`¿Eliminar ${selectedLeads.length} leads seleccionados? Esta acción no se puede deshacer.`)) {
      return;
    }
    
    setDeleting(true);
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    
    try {
      for (const leadId of selectedLeads) {
        await fetch(`${API}/api/clinic/${clinicId}/leads/${leadId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      setSelectedLeads([]);
      setSelectionMode(false);
      loadData();
      alert(`✅ ${selectedLeads.length} leads eliminados`);
    } catch (error) {
      console.error('Error:', error);
      alert('Error al eliminar leads');
    } finally {
      setDeleting(false);
    }
  };

  const bulkStartFollowUps = async () => {
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    
    // Helper function: lead NO debe tener follow-up si ya completó secuencia anterior
    const hasCompletedFollowUp = (lead) => {
      // Buscar si existe un follow-up completado (no_response o opted_out) para este teléfono
      const phone_suffix = lead.phone?.slice(-10);
      if (!phone_suffix) return false;
      
      return followUps.some(f => {
        const matches_phone = f.lead_phone?.includes(phone_suffix);
        const is_completed = f.status === 'no_response' || f.status === 'opted_out';
        return matches_phone && is_completed;
      });
    };
    
    // Use selected leads if in selection mode, otherwise use ONLY leads that never had a follow-up
    let leadsToStart;
    if (selectionMode && selectedLeads.length > 0) {
      leadsToStart = leads.filter(lead => 
        selectedLeads.includes(lead.id) && 
        !followUps.some(f => f.lead_phone?.includes(lead.phone?.slice(-10)))
      );
    } else {
      // SOLO leads que NUNCA han tenido un follow-up (ni activo ni completado)
      leadsToStart = leads.filter(lead => {
        const phone_suffix = lead.phone?.slice(-10);
        if (!phone_suffix) return false;
        
        // No debe tener NINGÚN follow-up (ni activo ni histórico)
        const hasAnyFollowUp = followUps.some(f => f.lead_phone?.includes(phone_suffix));
        return !hasAnyFollowUp;
      });
    }
    
    if (leadsToStart.length === 0) {
      alert('No hay leads sin follow-up previo. Solo se inician follow-ups en leads que nunca han tenido seguimiento.');
      return;
    }
    
    if (!window.confirm(`¿Iniciar secuencia de follow-up para ${leadsToStart.length} leads SIN SEGUIMIENTO PREVIO?`)) {
      return;
    }
    
    setBulkStarting(true);
    
    try {
      for (const lead of leadsToStart) {
        await fetch(`${API}/api/clinic/${clinicId}/follow-ups`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            lead_name: lead.name || 'Lead',
            lead_phone: lead.phone,
            lead_source: lead.source || 'whatsapp',
            notes: lead.ai_summary || ''
          })
        });
        // Small delay to avoid overwhelming the server
        await new Promise(r => setTimeout(r, 500));
      }
      setSelectedLeads([]);
      setSelectionMode(false);
      loadData();
      alert(`✅ ${leadsToStart.length} secuencias de follow-up iniciadas (solo leads nuevos)`);
    } catch (error) {
      console.error('Error:', error);
      alert('Error al iniciar follow-ups');
    } finally {
      setBulkStarting(false);
    }
  };

  const addNewLead = async () => {
    if (!newLead.name || !newLead.phone) {
      alert('Nombre y teléfono son requeridos');
      return;
    }
    
    setSaving(true);
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API}/api/clinic/${clinicId}/follow-ups`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lead_name: newLead.name,
          lead_phone: newLead.phone,
          lead_source: newLead.source,
          notes: newLead.notes
        })
      });
      
      if (response.ok) {
        setShowAddLead(false);
        setNewLead({ name: '', phone: '', source: 'manual', notes: '' });
        loadData();
        alert('✅ Lead agregado - El mensaje inicial se enviará en 1 minuto (si es horario laboral)');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  // Find the underlying lead (in `leads` collection) that matches a follow-up by phone
  const leadFromFollowUp = (f) => {
    if (!f?.lead_phone) return null;
    const suffix = String(f.lead_phone).replace(/\D/g, '').slice(-10);
    if (!suffix) return null;
    return leads.find(l => String(l.phone || '').replace(/\D/g, '').endsWith(suffix)) || null;
  };

  const openOutcome = (lead) => {
    if (!lead) return;
    const o = lead.outcome || {};
    setOutcomeDraft({
      attended: !!o.attended,
      consultation_revenue: o.consultation_revenue || '',
      converted_to_surgery: !!o.converted_to_surgery,
      surgery_type: o.surgery_type || 'lasik',
      surgery_revenue: o.surgery_revenue || '',
      notes: o.notes || '',
    });
    setOutcomeOpenFor(lead.id);
  };

  const saveOutcome = async (leadId) => {
    setSavingOutcome(true);
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      const body = {
        attended: !!outcomeDraft.attended,
        consultation_revenue: outcomeDraft.attended ? Number(outcomeDraft.consultation_revenue || 0) : 0,
        converted_to_surgery: !!outcomeDraft.converted_to_surgery,
        surgery_type: outcomeDraft.converted_to_surgery ? outcomeDraft.surgery_type : null,
        surgery_revenue: outcomeDraft.converted_to_surgery ? Number(outcomeDraft.surgery_revenue || 0) : 0,
        notes: outcomeDraft.notes || '',
      };
      const res = await fetch(`${API}/api/clinic/${clinicId}/leads/${leadId}/outcome`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Error al guardar: ${err.detail || res.statusText}`);
        return;
      }
      setOutcomeOpenFor(null);
      await loadData();
    } catch (e) {
      console.error('Error saving outcome:', e);
      alert('Error al guardar el ingreso');
    } finally {
      setSavingOutcome(false);
    }
  };

  // Función para actualizar un campo de un paciente en la tabla de consultas gestionadas
  const updateConvertedField = async (leadId, field, value) => {
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      
      // Encontrar el lead actual para preservar sus datos
      const currentLead = leads.find(l => l.id === leadId);
      if (!currentLead) {
        alert('Lead no encontrado');
        return;
      }

      const outcome = currentLead.outcome || {};
      
      // Preparar el body basado en el campo que se está editando
      let body = {
        attended: outcome.attended || false,
        consultation_revenue: Number(outcome.consultation_revenue || 0),
        converted_to_surgery: outcome.converted_to_surgery || false,
        surgery_type: outcome.surgery_type || 'lasik',
        surgery_revenue: Number(outcome.surgery_revenue || 0),
        notes: outcome.notes || ''
      };

      // Actualizar el campo específico
      if (field === 'name') {
        // Si es el nombre, actualizar el lead directamente (no es parte del outcome)
        await fetch(`${API}/api/clinic/${clinicId}/leads/${leadId}`, {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: value })
        });
        await loadData();
        return;
      } else if (field === 'phone') {
        await fetch(`${API}/api/clinic/${clinicId}/leads/${leadId}`, {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: value })
        });
        await loadData();
        return;
      } else if (field === 'concept') {
        // Cambiar el concepto (consulta vs cirugía)
        if (value === 'consulta') {
          body.converted_to_surgery = false;
          body.surgery_type = null;
          body.surgery_revenue = 0;
          body.attended = true;
        } else {
          body.converted_to_surgery = true;
          body.surgery_type = value;
          body.attended = true;
        }
      } else if (field === 'consultation_revenue') {
        body.consultation_revenue = Number(value) || 0;
        body.attended = true;
      } else if (field === 'surgery_revenue') {
        body.surgery_revenue = Number(value) || 0;
        if (body.surgery_revenue > 0) {
          body.converted_to_surgery = true;
        }
      }

      const res = await fetch(`${API}/api/clinic/${clinicId}/leads/${leadId}/outcome`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Error al actualizar: ${err.detail || res.statusText}`);
        return;
      }

      await loadData(); // Recargar datos para actualizar totales
    } catch (e) {
      console.error('Error updating field:', e);
      alert('Error al actualizar el campo');
    }
  };

  // Función para añadir un nuevo paciente directamente a la tabla
  const addNewPatientToTable = async () => {
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      
      // Generar un teléfono temporal único (timestamp para evitar duplicados)
      const tempPhone = `521${Date.now()}`.slice(0, 13);
      
      // Crear un nuevo lead manualmente
      const newLeadData = {
        name: 'Nuevo Paciente',
        phone: tempPhone,
        source: 'manual',
        notes: 'Paciente agregado directamente desde consultas gestionadas'
      };

      // Primero crear el lead
      const createRes = await fetch(`${API}/api/clinic/${clinicId}/leads`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newLeadData)
      });

      if (!createRes.ok) {
        const errorText = await createRes.text();
        console.error('Error creating lead:', errorText);
        alert(`Error al crear el paciente nuevo: ${errorText}`);
        return;
      }

      const createdLead = await createRes.json();
      const leadId = createdLead.id;

      if (!leadId) {
        console.error('No lead ID returned:', createdLead);
        alert('Error: No se recibió ID del lead creado');
        return;
      }

      // Ahora registrar un outcome básico para que aparezca en consultas gestionadas
      const outcomeBody = {
        attended: true,
        consultation_revenue: 0,
        converted_to_surgery: false,
        surgery_type: null,
        surgery_revenue: 0,
        notes: ''
      };

      const outcomeRes = await fetch(`${API}/api/clinic/${clinicId}/leads/${leadId}/outcome`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(outcomeBody)
      });

      if (!outcomeRes.ok) {
        const errorText = await outcomeRes.text();
        console.error('Error setting outcome:', errorText);
        alert(`Error al registrar el ingreso del paciente nuevo: ${errorText}`);
        return;
      }

      await loadData(); // Recargar datos
      setAddingNewPatient(false);
    } catch (e) {
      console.error('Error adding new patient:', e);
      alert(`Error al añadir paciente nuevo: ${e.message}`);
    } finally {
      setAddingNewPatient(false);
    }
  };

  const OutcomePanel = ({ lead }) => {
    if (!lead) return null;
    const isOpen = outcomeOpenFor === lead.id;
    if (!isOpen) return null;
    const draft = outcomeDraft;
    return (
      <div className={`mt-3 p-4 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50 border border-slate-600' : 'bg-emerald-50 border border-emerald-200'}`}>
        <div className="flex items-center justify-between mb-3">
          <h4 className={`${textPrimary} font-semibold text-sm flex items-center gap-2`}>
            <DollarSign className="w-4 h-4 text-emerald-500" />
            Registrar ingreso de {lead.name || lead.phone}
          </h4>
          <button onClick={() => setOutcomeOpenFor(null)} className={textMuted}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          {/* Asistió a consulta */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!draft.attended}
                onChange={(e) => setOutcomeDraft({ ...draft, attended: e.target.checked })}
                className="rounded"
              />
              <span className={`${textPrimary} text-sm font-medium`}>¿Asistió a consulta?</span>
            </label>
            {draft.attended && (
              <div className="mt-2 ml-6 flex items-center gap-2">
                <span className={`${textSecondary} text-sm`}>Ingreso por consulta (MXN):</span>
                <Input
                  type="number"
                  min="0"
                  value={draft.consultation_revenue}
                  onChange={(e) => setOutcomeDraft({ ...draft, consultation_revenue: e.target.value })}
                  className={`${inputBg} max-w-[160px]`}
                  placeholder="Ej: 800"
                />
              </div>
            )}
          </div>

          {/* Convirtió a cirugía */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!draft.converted_to_surgery}
                onChange={(e) => setOutcomeDraft({ ...draft, converted_to_surgery: e.target.checked })}
                className="rounded"
              />
              <span className={`${textPrimary} text-sm font-medium`}>¿Convirtió a cirugía?</span>
            </label>
            {draft.converted_to_surgery && (
              <div className="mt-2 ml-6 flex flex-wrap items-center gap-2">
                <select
                  value={draft.surgery_type}
                  onChange={(e) => setOutcomeDraft({ ...draft, surgery_type: e.target.value })}
                  className={`${inputBg} rounded-md px-3 py-2 text-sm`}
                >
                  {SURGERY_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <span className={`${textSecondary} text-sm`}>Ingreso por cirugía (MXN):</span>
                <Input
                  type="number"
                  min="0"
                  value={draft.surgery_revenue}
                  onChange={(e) => setOutcomeDraft({ ...draft, surgery_revenue: e.target.value })}
                  className={`${inputBg} max-w-[180px]`}
                  placeholder="Ej: 25000"
                />
              </div>
            )}
          </div>

          {/* Notas opcionales */}
          <Input
            placeholder="Notas (opcional)"
            value={draft.notes || ''}
            onChange={(e) => setOutcomeDraft({ ...draft, notes: e.target.value })}
            className={inputBg}
          />

          <div className="flex gap-2 pt-1">
            <Button
              onClick={() => saveOutcome(lead.id)}
              disabled={savingOutcome}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              size="sm"
            >
              {savingOutcome ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar ingreso'}
            </Button>
            <Button variant="ghost" onClick={() => setOutcomeOpenFor(null)} size="sm">Cancelar</Button>
          </div>
        </div>
      </div>
    );
  };

  const markResponded = async (leadId) => {
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    
    try {
      await fetch(`${API}/api/clinic/${clinicId}/follow-ups/${leadId}/mark-responded`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadData();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const optOut = async (leadId) => {
    if (!window.confirm('¿Marcar este lead como opt-out? No recibirá más mensajes.')) return;
    
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    
    try {
      await fetch(`${API}/api/clinic/${clinicId}/follow-ups/${leadId}/opt-out`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadData();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    return date.toLocaleString('es-MX', { 
      day: '2-digit', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true
    });
  };

  const getTimeAgo = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `hace ${diffMins} min`;
    if (diffHours < 24) return `hace ${diffHours}h`;
    return `hace ${diffDays}d`;
  };

  const getStatusBadge = (status) => {
    const config = {
      new: { color: 'bg-blue-500/20 text-blue-400 border border-blue-500/30', icon: Clock, label: 'Pendiente envío' },
      contacted: { color: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30', icon: Send, label: 'Msg 1 enviado' },
      follow_up_1: { color: 'bg-orange-500/20 text-orange-400 border border-orange-500/30', icon: RefreshCw, label: 'Msg 2 enviado' },
      follow_up_2: { color: 'bg-purple-500/20 text-purple-400 border border-purple-500/30', icon: RefreshCw, label: 'Msg 3 enviado' },
      responded: { color: 'bg-green-500/20 text-green-400 border border-green-500/30', icon: CheckCircle, label: 'Respondió' },
      no_response: { color: 'bg-gray-500/20 text-gray-400 border border-gray-500/30', icon: XCircle, label: 'Secuencia completada' },
      opted_out: { color: 'bg-red-500/20 text-red-400 border border-red-500/30', icon: XCircle, label: 'Opt-out' }
    };
    const c = config[status] || config.new;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${c.color}`}>
        <c.icon className="w-3 h-3" />
        {c.label}
      </span>
    );
  };

  const getSourceConfig = (source) => {
    const config = {
      whatsapp: { icon: '💬', label: 'WhatsApp', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
      whatsapp_twilio: { icon: '💬', label: 'WhatsApp', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
      meta_ads: { icon: '📘', label: 'Meta Ads', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      facebook_ad: { icon: '📘', label: 'Facebook Ad', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      facebook_form: { icon: '📋', label: 'FB Form', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      instagram_comment: { icon: '📷', label: 'IG Comment', color: 'bg-pink-500/20 text-pink-400 border-pink-500/30' },
      instagram_dm: { icon: '📷', label: 'IG DM', color: 'bg-pink-500/20 text-pink-400 border-pink-500/30' },
      facebook_dm: { icon: '📘', label: 'FB DM', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      google_ads: { icon: '🔍', label: 'Google Ads', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
      tiktok: { icon: '🎵', label: 'TikTok', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
      tiktok_ads: { icon: '🎵', label: 'TikTok Ads', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
      organic: { icon: '🌱', label: 'Orgánico', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
      website: { icon: '🌐', label: 'Website', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
      manual: { icon: '✏️', label: 'Manual', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
      direct_booking: { icon: '📅', label: 'Reserva', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
      messenger_dm: { icon: '💬', label: 'Messenger', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' }
    };
    return config[source] || { icon: '📱', label: source || 'Desconocido', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' };
  };

  const SourceBadge = ({ source }) => {
    const config = getSourceConfig(source);
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${config.color}`}>
        <span>{config.icon}</span>
        <span>{config.label}</span>
      </span>
    );
  };

  const getSourceIcon = (source) => {
    return getSourceConfig(source).icon;
  };

  // Funnel badge component
  const FunnelBadge = ({ funnel }) => {
    if (!funnel) return null;
    
    const { has_appointment, attended_consultation, is_converted, scheduled_count, completed_count } = funnel;
    
    if (is_converted) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-400 border border-green-500/30">
          <span>💰</span>
          <span>Convertido</span>
        </span>
      );
    }
    
    if (attended_consultation || completed_count > 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
          <CheckCircle className="w-3 h-3" />
          <span>Asistió a consulta</span>
        </span>
      );
    }
    
    if (has_appointment && scheduled_count > 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
          <Calendar className="w-3 h-3" />
          <span>Cita agendada</span>
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
        <Users className="w-3 h-3" />
        <span>Lead nuevo</span>
      </span>
    );
  };

  const filteredLeads = leads.filter(lead => {
    if (filter !== 'all' && lead.status !== filter) return false;
    if (sourceFilter !== 'all' && lead.source !== sourceFilter) return false;
    
    // Funnel filter
    if (funnelFilter !== 'all') {
      const funnel = lead.funnel || {};
      if (funnelFilter === 'new' && (funnel.has_appointment || funnel.attended_consultation || funnel.is_converted)) return false;
      if (funnelFilter === 'scheduled' && (!funnel.has_appointment || funnel.scheduled_count === 0 || funnel.attended_consultation)) return false;
      if (funnelFilter === 'attended' && !funnel.attended_consultation) return false;
      if (funnelFilter === 'converted' && !funnel.is_converted) return false;
    }
    
    if (search) {
      const s = search.toLowerCase();
      return (
        lead.name?.toLowerCase().includes(s) || 
        lead.phone?.includes(search) ||
        lead.interested_service?.toLowerCase().includes(s) ||
        lead.notes?.toLowerCase().includes(s) ||
        lead.ai_summary?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const filteredFollowUps = followUps.filter(f => {
    if (filter !== 'all' && f.status !== filter) return false;
    if (sourceFilter !== 'all' && f.lead_source !== sourceFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        f.lead_name?.toLowerCase().includes(s) || 
        f.lead_phone?.includes(search) ||
        f.notes?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  // Count leads that NEVER had a follow-up (ni activo ni histórico)
  const leadsWithoutFollowUp = leads.filter(lead => {
    const phone_suffix = lead.phone?.slice(-10);
    if (!phone_suffix) return false;
    // NO debe tener NINGÚN follow-up en el historial
    return !followUps.some(f => f.lead_phone?.includes(phone_suffix));
  }).length;

  if (loading) {
    return (
      <DashboardLayout title="Leads & Follow-ups">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Leads & Follow-ups">
      {/* Scheduler Status Indicator */}
      <Card className={`${cardBg} mb-4 border-l-4 border-l-green-500`}>
        <CardContent className="p-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <div>
              <p className={`${textPrimary} font-medium text-sm`}>Proceso automático ACTIVO</p>
              <p className={`${textMuted} text-xs`}>
                Mensajes se envían 24/7 (respuesta inmediata) | Day 0 → +24h → Day 3 → Day 7
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Backup Config Button */}
            <button
              onClick={() => setShowBackupConfig(!showBackupConfig)}
              className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${
                backupConfig.enabled 
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                  : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
              }`}
            >
              <Copy className="w-3 h-3" />
              Backup: {backupConfig.enabled ? 'ON' : 'OFF'}
            </button>
            <div className={`${textMuted} text-xs`}>
              <Clock className="w-3 h-3 inline mr-1" />
              Verifica cada 5 min
            </div>
          </div>
        </CardContent>
        
        {/* Backup Config Panel */}
        {showBackupConfig && (
          <div className={`px-4 pb-4 border-t ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
            <div className="pt-3 space-y-3">
              <p className={`text-sm font-medium ${textPrimary}`}>
                <Copy className="w-4 h-4 inline mr-2" />
                Configuración de Backup de Mensajes
              </p>
              <p className={`text-xs ${textMuted}`}>
                Recibe una copia de cada mensaje de follow-up en tu WhatsApp
              </p>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={backupConfig.enabled}
                    onChange={(e) => setBackupConfig({ ...backupConfig, enabled: e.target.checked })}
                    className="rounded"
                  />
                  <span className={`text-sm ${textSecondary}`}>Activar backup</span>
                </label>
                <input
                  type="text"
                  value={backupConfig.backup_phone}
                  onChange={(e) => setBackupConfig({ ...backupConfig, backup_phone: e.target.value })}
                  placeholder="Número WhatsApp (10 dígitos)"
                  className={`flex-1 px-3 py-1 rounded text-sm ${inputBg} max-w-xs`}
                />
                <Button
                  size="sm"
                  onClick={saveBackupConfig}
                  disabled={savingBackup}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {savingBackup ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className={cardBg}>
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 mx-auto mb-2 text-blue-500" />
              <p className={`text-2xl font-bold ${textPrimary}`}>{stats.total}</p>
              <p className={`text-xs ${textMuted}`}>En Follow-up</p>
            </CardContent>
          </Card>
          <Card className={cardBg}>
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 mx-auto mb-2 text-yellow-500" />
              <p className={`text-2xl font-bold ${textPrimary}`}>{stats.in_progress}</p>
              <p className={`text-xs ${textMuted}`}>En Proceso</p>
            </CardContent>
          </Card>
          <Card className={cardBg}>
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-500" />
              <p className={`text-2xl font-bold ${textPrimary}`}>{stats.responded}</p>
              <p className={`text-xs ${textMuted}`}>Respondieron</p>
            </CardContent>
          </Card>
          <Card className={cardBg}>
            <CardContent className="p-4 text-center">
              <XCircle className="w-6 h-6 mx-auto mb-2 text-gray-500" />
              <p className={`text-2xl font-bold ${textPrimary}`}>{stats.no_response}</p>
              <p className={`text-xs ${textMuted}`}>Sin Respuesta</p>
            </CardContent>
          </Card>
          <Card className={cardBg}>
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-6 h-6 mx-auto mb-2 text-purple-500" />
              <p className={`text-2xl font-bold ${textPrimary}`}>{stats.conversion_rate}%</p>
              <p className={`text-xs ${textMuted}`}>Conversión</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Funnel Stats */}
      {funnelStats && (
        <Card className={`${cardBg} mb-6`}>
          <CardContent className="p-4">
            <h3 className={`${textPrimary} font-semibold flex items-center gap-2 mb-4`}>
              <TrendingUp className="w-5 h-5 text-green-500" />
              Embudo de Conversión
            </h3>
            <div className="flex items-center justify-between gap-2 overflow-x-auto pb-2">
              {/* Lead nuevo */}
              <button 
                onClick={() => setFunnelFilter(funnelFilter === 'new' ? 'all' : 'new')}
                className={`flex-1 min-w-[120px] p-3 rounded-lg text-center cursor-pointer transition-all hover:scale-105 ${
                  funnelFilter === 'new' ? 'ring-2 ring-blue-500' : ''
                } ${theme === 'dark' ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-blue-50 border border-blue-200'}`}
              >
                <Users className="w-6 h-6 mx-auto mb-1 text-blue-500" />
                <p className={`text-xl font-bold ${textPrimary}`}>{funnelStats.total_leads}</p>
                <p className={`text-xs ${textMuted}`}>Leads</p>
              </button>
              
              <div className={`${textMuted} text-2xl`}>→</div>
              
              {/* Con cita */}
              <button 
                onClick={() => setFunnelFilter(funnelFilter === 'scheduled' ? 'all' : 'scheduled')}
                className={`flex-1 min-w-[120px] p-3 rounded-lg text-center cursor-pointer transition-all hover:scale-105 ${
                  funnelFilter === 'scheduled' ? 'ring-2 ring-yellow-500' : ''
                } ${theme === 'dark' ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-yellow-50 border border-yellow-200'}`}
              >
                <Calendar className="w-6 h-6 mx-auto mb-1 text-yellow-500" />
                <p className={`text-xl font-bold ${textPrimary}`}>{funnelStats.with_appointment}</p>
                <p className={`text-xs ${textMuted}`}>Con cita</p>
                <p className={`text-xs text-yellow-500`}>{funnelStats.funnel_rates?.lead_to_appointment || 0}%</p>
              </button>
              
              <div className={`${textMuted} text-2xl`}>→</div>
              
              {/* Asistió */}
              <button 
                onClick={() => setFunnelFilter(funnelFilter === 'attended' ? 'all' : 'attended')}
                className={`flex-1 min-w-[120px] p-3 rounded-lg text-center cursor-pointer transition-all hover:scale-105 ${
                  funnelFilter === 'attended' ? 'ring-2 ring-emerald-500' : ''
                } ${theme === 'dark' ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200'}`}
              >
                <CheckCircle className="w-6 h-6 mx-auto mb-1 text-emerald-500" />
                <p className={`text-xl font-bold ${textPrimary}`}>{funnelStats.attended_consultation}</p>
                <p className={`text-xs ${textMuted}`}>Asistió</p>
                <p className={`text-xs text-emerald-500`}>{funnelStats.funnel_rates?.appointment_to_attended || 0}%</p>
              </button>
              
              <div className={`${textMuted} text-2xl`}>→</div>
              
              {/* Convertido */}
              <button 
                onClick={() => setFunnelFilter(funnelFilter === 'converted' ? 'all' : 'converted')}
                className={`flex-1 min-w-[120px] p-3 rounded-lg text-center cursor-pointer transition-all hover:scale-105 ${
                  funnelFilter === 'converted' ? 'ring-2 ring-green-500' : ''
                } ${theme === 'dark' ? 'bg-green-500/10 border border-green-500/30' : 'bg-green-50 border border-green-200'}`}
              >
                <span className="text-2xl">💰</span>
                <p className={`text-xl font-bold ${textPrimary}`}>{funnelStats.converted}</p>
                <p className={`text-xs ${textMuted}`}>Convertido</p>
                <p className={`text-xs text-green-500`}>{funnelStats.funnel_rates?.overall_conversion || 0}%</p>
              </button>
            </div>
            
            {/* Revenue metrics — siempre visible para que el doctor sepa dónde registrar */}
            <div className={`mt-4 pt-4 border-t ${theme === 'dark' ? 'border-slate-600' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <p className={`text-xs font-semibold uppercase tracking-wide ${textMuted}`}>Ingresos registrados</p>
                <p className={`text-xs ${textMuted}`}>Usa el botón "Registrar ingreso" en cada lead para agregar datos</p>
              </div>
              <div className="flex flex-wrap gap-4">
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200'}`}>
                  <span className="text-xl">🩺</span>
                  <div>
                    <p className={`text-lg font-bold ${textPrimary}`}>{fmtMoney(funnelStats.consultation_revenue_total)}</p>
                    <p className={`text-xs ${textMuted}`}>Ingresos por consulta</p>
                  </div>
                </div>
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-purple-50 border border-purple-200'}`}>
                  <span className="text-xl">⚕️</span>
                  <div>
                    <p className={`text-lg font-bold ${textPrimary}`}>{fmtMoney(funnelStats.surgery_revenue_total)}</p>
                    <p className={`text-xs ${textMuted}`}>Ingresos por cirugía</p>
                  </div>
                </div>
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-green-500/10 border border-green-500/30' : 'bg-green-50 border border-green-200'}`}>
                  <span className="text-xl">💰</span>
                  <div>
                    <p className={`text-lg font-bold ${textPrimary}`}>
                      {fmtMoney(funnelStats.total_revenue || funnelStats.estimated_revenue || 0)}
                    </p>
                    <p className={`text-xs ${textMuted}`}>Total ingresos</p>
                  </div>
                </div>
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-blue-50 border border-blue-200'}`}>
                  <span className="text-xl">🏥</span>
                  <div>
                    <p className={`text-lg font-bold ${textPrimary}`}>{funnelStats.completed_appointments || 0}</p>
                    <p className={`text-xs ${textMuted}`}>Consultas completadas</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabla de convertidos — siempre visible y editable inline */}
            <div className={`mt-4 pt-4 border-t ${theme === 'dark' ? 'border-slate-600' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <h4 className={`${textPrimary} font-semibold flex items-center gap-2`}>
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Consultas gestionadas ({(funnelStats.converted_list || []).length})
                </h4>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`text-left ${textMuted} border-b ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
                      <th className="py-2 pr-3 flex items-center gap-2">
                        Paciente
                        <button
                          onClick={() => {
                            if (addingNewPatient) return;
                            setAddingNewPatient(true);
                            addNewPatientToTable();
                          }}
                          disabled={addingNewPatient}
                          className={`ml-1 w-5 h-5 rounded-full ${addingNewPatient ? 'bg-gray-400' : 'bg-green-500 hover:bg-green-600'} text-white flex items-center justify-center transition-colors`}
                          title="Añadir paciente nuevo"
                        >
                          {addingNewPatient ? <Loader2 className="w-3 h-3 animate-spin" /> : <span className="text-xs font-bold">+</span>}
                        </button>
                      </th>
                      <th className="py-2 pr-3">Teléfono</th>
                      <th className="py-2 pr-3">Concepto</th>
                      <th className="py-2 pr-3 text-right">Consulta</th>
                      <th className="py-2 pr-3 text-right">Cirugía</th>
                      <th className="py-2 pr-3 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(!funnelStats.converted_list || funnelStats.converted_list.length === 0) ? (
                      <tr>
                        <td colSpan={6} className={`py-6 text-center text-sm ${textMuted} italic`}>
                          Aún no hay consultas registradas. Presiona el botón "+" para añadir la primera.
                        </td>
                      </tr>
                    ) : (
                      funnelStats.converted_list.map((c, i) => {
                        const total = (c.consultation_revenue || 0) + (c.surgery_revenue || 0);
                        const hasSurgery = c.surgery_type && c.surgery_revenue > 0;
                        return (
                          <tr key={c.lead_id || i} className={`${textSecondary} border-b ${theme === 'dark' ? 'border-slate-800' : 'border-gray-100'} hover:bg-slate-700/30 transition-colors`}>
                            {/* Nombre editable al hacer click */}
                            <td className="py-2 pr-3">
                              <input
                                type="text"
                                defaultValue={c.lead_name}
                                className={`${inputBg} px-2 py-1 rounded w-full border-0 focus:ring-1 focus:ring-blue-500`}
                                placeholder="Nombre del paciente"
                                onBlur={(e) => {
                                  if (e.target.value !== c.lead_name) {
                                    updateConvertedField(c.lead_id, 'name', e.target.value);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.target.blur();
                                  }
                                }}
                              />
                            </td>
                            {/* Teléfono editable */}
                            <td className="py-2 pr-3">
                              <input
                                type="text"
                                defaultValue={c.lead_phone}
                                className={`${inputBg} px-2 py-1 rounded w-full border-0 focus:ring-1 focus:ring-blue-500`}
                                placeholder="Teléfono"
                                onBlur={(e) => {
                                  if (e.target.value !== c.lead_phone) {
                                    updateConvertedField(c.lead_id, 'phone', e.target.value);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.target.blur();
                                  }
                                }}
                              />
                            </td>
                            {/* Concepto editable (dropdown) */}
                            <td className="py-2 pr-3">
                              <select
                                defaultValue={hasSurgery ? c.surgery_type : 'consulta'}
                                className={`${inputBg} px-2 py-1 rounded text-xs border-0 focus:ring-1 focus:ring-blue-500`}
                                onChange={(e) => {
                                  updateConvertedField(c.lead_id, 'concept', e.target.value);
                                }}
                              >
                                <option value="consulta">Solo consulta</option>
                                {SURGERY_OPTIONS.map(o => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                            </td>
                            {/* Monto consulta editable */}
                            <td className="py-2 pr-3 text-right">
                              <input
                                type="number"
                                min="0"
                                step="100"
                                defaultValue={c.consultation_revenue || 0}
                                className={`${inputBg} px-2 py-1 rounded w-24 text-right border-0 focus:ring-1 focus:ring-blue-500`}
                                onBlur={(e) => {
                                  const newValue = Number(e.target.value) || 0;
                                  if (newValue !== (c.consultation_revenue || 0)) {
                                    updateConvertedField(c.lead_id, 'consultation_revenue', newValue);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.target.blur();
                                  }
                                }}
                              />
                            </td>
                            {/* Monto cirugía editable */}
                            <td className="py-2 pr-3 text-right">
                              <input
                                type="number"
                                min="0"
                                step="1000"
                                defaultValue={c.surgery_revenue || 0}
                                className={`${inputBg} px-2 py-1 rounded w-28 text-right border-0 focus:ring-1 focus:ring-blue-500`}
                                onBlur={(e) => {
                                  const newValue = Number(e.target.value) || 0;
                                  if (newValue !== (c.surgery_revenue || 0)) {
                                    updateConvertedField(c.lead_id, 'surgery_revenue', newValue);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.target.blur();
                                  }
                                }}
                              />
                            </td>
                            {/* Total calculado */}
                            <td className={`py-2 pr-3 text-right font-semibold ${textPrimary}`}>
                              {fmtMoney(total)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  <tfoot>
                    <tr className={`${textPrimary} font-semibold border-t-2 ${theme === 'dark' ? 'border-slate-600' : 'border-gray-300'}`}>
                      <td className="pt-2" colSpan={3}>Totales</td>
                      <td className="pt-2 text-right">{fmtMoney(funnelStats.consultation_revenue_total || 0)}</td>
                      <td className="pt-2 text-right">{fmtMoney(funnelStats.surgery_revenue_total || 0)}</td>
                      <td className="pt-2 text-right">{fmtMoney(funnelStats.total_revenue || 0)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              
              {/* Breakdown por tipo de cirugía */}
              {funnelStats.surgery_breakdown && Object.keys(funnelStats.surgery_breakdown).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {Object.entries(funnelStats.surgery_breakdown).map(([k, v]) => (
                    <span key={k} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-purple-500/10 text-purple-400 border border-purple-500/30">
                      {surgeryLabel(k)}: <strong>{v}</strong>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Active funnel filter indicator */}
            {funnelFilter !== 'all' && (
              <div className="mt-3 flex items-center gap-2">
                <span className={`text-sm ${textMuted}`}>Filtro activo:</span>
                <button
                  onClick={() => setFunnelFilter('all')}
                  className="flex items-center gap-1 px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm border border-blue-500/30 hover:bg-blue-500/30 transition-colors"
                >
                  {funnelFilter === 'new' && '🔵 Leads nuevos'}
                  {funnelFilter === 'scheduled' && '📅 Con cita agendada'}
                  {funnelFilter === 'attended' && '✅ Asistió a consulta'}
                  {funnelFilter === 'converted' && '💰 Convertidos'}
                  <X className="w-3 h-3 ml-1" />
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Leads Source Breakdown */}
      {leadsStats?.source_breakdown && Object.keys(leadsStats.source_breakdown).length > 0 && (
        <Card className={`${cardBg} mb-6`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className={`${textPrimary} font-semibold flex items-center gap-2`}>
                <TrendingUp className="w-5 h-5 text-blue-500" />
                Origen de Leads ({leadsStats.total} total)
              </h3>
              <div className="flex items-center gap-2 text-xs">
                <span className={`${textMuted}`}>Hoy: {leadsStats.new_today}</span>
                <span className={textMuted}>|</span>
                <span className={`${textMuted}`}>Esta semana: {leadsStats.new_week}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {Object.entries(leadsStats.source_breakdown)
                .sort((a, b) => b[1] - a[1])
                .map(([source, count]) => {
                  const config = getSourceConfig(source);
                  const percentage = leadsStats.total > 0 ? Math.round((count / leadsStats.total) * 100) : 0;
                  const isActive = sourceFilter === source;
                  return (
                    <button 
                      key={source} 
                      onClick={() => setSourceFilter(isActive ? 'all' : source)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all cursor-pointer hover:scale-105 ${config.color} ${isActive ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
                    >
                      <span className="text-lg">{config.icon}</span>
                      <div className="text-left">
                        <p className="font-medium text-sm">{config.label}</p>
                        <p className={`text-xs ${textMuted}`}>{count} leads ({percentage}%)</p>
                      </div>
                    </button>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-200 dark:border-slate-700">
        <button
          onClick={() => setActiveTab('leads')}
          className={`pb-3 px-2 font-medium ${activeTab === 'leads' ? 'border-b-2 border-blue-500 text-blue-500' : textSecondary}`}
        >
          <Users className="w-4 h-4 inline mr-2" />
          Leads ({leads.length})
        </button>
        <button
          onClick={() => setActiveTab('followups')}
          className={`pb-3 px-2 font-medium ${activeTab === 'followups' ? 'border-b-2 border-blue-500 text-blue-500' : textSecondary}`}
        >
          <Send className="w-4 h-4 inline mr-2" />
          Follow-ups ({followUps.length})
        </button>
      </div>

      {/* Filters & Add Button */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${textMuted}`} />
          <Input
            placeholder="Buscar por nombre, teléfono o procedimiento..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${inputBg} pl-10`}
          />
        </div>
        
        {/* Source Filter Dropdown */}
        <div className="relative">
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className={`${inputBg} rounded-md px-3 py-2 pr-8 appearance-none cursor-pointer min-w-[160px] border`}
          >
            <option value="all">📊 Todas las fuentes</option>
            <option value="whatsapp">💬 WhatsApp</option>
            <option value="meta_ads">📘 Meta Ads</option>
            <option value="google_ads">🔍 Google Ads</option>
            <option value="tiktok">🎵 TikTok</option>
            <option value="instagram_dm">📷 Instagram DM</option>
            <option value="facebook_dm">📘 Facebook DM</option>
            <option value="organic">🌱 Orgánico</option>
            <option value="website">🌐 Website</option>
            <option value="manual">✏️ Manual</option>
          </select>
          <Filter className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 ${textMuted} pointer-events-none`} />
        </div>

        {/* Active filter badge */}
        {sourceFilter !== 'all' && (
          <button
            onClick={() => setSourceFilter('all')}
            className="flex items-center gap-1 px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm border border-blue-500/30 hover:bg-blue-500/30 transition-colors"
          >
            <span>{getSourceConfig(sourceFilter).icon}</span>
            <span>{getSourceConfig(sourceFilter).label}</span>
            <X className="w-3 h-3 ml-1" />
          </button>
        )}

          {activeTab === 'leads' && (
            <>
              {/* Selection Mode Toggle */}
              <Button 
                onClick={() => {
                  setSelectionMode(!selectionMode);
                  setSelectedLeads([]);
                }}
                variant={selectionMode ? "default" : "outline"}
                className={selectionMode ? "bg-purple-600 hover:bg-purple-700 text-white" : ""}
              >
                {selectionMode ? <CheckSquare className="w-4 h-4 mr-2" /> : <Square className="w-4 h-4 mr-2" />}
                {selectionMode ? 'Cancelar' : 'Seleccionar'}
              </Button>

              {/* Selection Actions */}
              {selectionMode && (
                <>
                  <Button 
                    onClick={selectAllLeads}
                    variant="outline"
                  >
                    {selectedLeads.length === leadsWithoutFollowUp ? 'Deseleccionar' : 'Seleccionar todos'}
                  </Button>
                  
                  {selectedLeads.length > 0 && (
                    <>
                      <Button 
                        onClick={bulkStartFollowUps} 
                        disabled={bulkStarting}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        {bulkStarting ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Zap className="w-4 h-4 mr-2" />
                        )}
                        Iniciar {selectedLeads.length}
                      </Button>
                      
                      <Button 
                        onClick={deleteSelectedLeads}
                        disabled={deleting}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        {deleting ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Trash2 className="w-4 h-4 mr-2" />
                        )}
                        Eliminar {selectedLeads.length}
                      </Button>
                    </>
                  )}
                </>
              )}

              {/* Quick start all (when not in selection mode) */}
              {!selectionMode && leadsWithoutFollowUp > 0 && (
                <Button 
                  onClick={bulkStartFollowUps} 
                  disabled={bulkStarting}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {bulkStarting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Zap className="w-4 h-4 mr-2" />
                  )}
                  Iniciar {leadsWithoutFollowUp} pendientes
                </Button>
              )}
            </>
          )}
          <Button onClick={() => setShowAddLead(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
            <UserPlus className="w-4 h-4 mr-2" />
            Agregar Lead
          </Button>
          <Button 
            onClick={() => {
              const clinicId = localStorage.getItem('clinicId');
              const token = localStorage.getItem('token');
              window.open(`${API}/api/clinic/${clinicId}/follow-ups/export?token=${token}`, '_blank');
            }}
            variant="outline"
            className={theme === 'dark' ? 'border-slate-600' : 'border-gray-300'}
          >
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
      </div>

      {/* Add Lead Modal */}
      {showAddLead && (
        <Card className={`${cardBg} mb-6`}>
          <CardContent className="p-4 space-y-4">
            <h3 className={`font-semibold ${textPrimary}`}>Agregar Nuevo Lead</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                placeholder="Nombre"
                value={newLead.name}
                onChange={(e) => setNewLead({...newLead, name: e.target.value})}
                className={inputBg}
              />
              <Input
                placeholder="Teléfono (ej: 5215541234567)"
                value={newLead.phone}
                onChange={(e) => setNewLead({...newLead, phone: e.target.value})}
                className={inputBg}
              />
              <select
                value={newLead.source}
                onChange={(e) => setNewLead({...newLead, source: e.target.value})}
                className={`${inputBg} rounded-md px-3 py-2`}
              >
                <option value="manual">Manual</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="facebook_ad">Facebook Ad</option>
                <option value="instagram_comment">Instagram</option>
                <option value="website">Website</option>
              </select>
              <Input
                placeholder="Notas (opcional)"
                value={newLead.notes}
                onChange={(e) => setNewLead({...newLead, notes: e.target.value})}
                className={inputBg}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={addNewLead} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Agregar e Iniciar Follow-up'}
              </Button>
              <Button variant="ghost" onClick={() => setShowAddLead(false)}>Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Follow-ups Tab */}
      {activeTab === 'followups' && (
        <div className="space-y-3">
          {filteredFollowUps.length === 0 ? (
            <Card className={cardBg}>
              <CardContent className="py-12 text-center">
                <Send className={`w-12 h-12 ${textMuted} mx-auto mb-4`} />
                <p className={textSecondary}>No hay follow-ups activos</p>
                <p className={`${textMuted} text-sm mt-1`}>Ve a la pestaña "Leads" y presiona "Iniciar pendientes"</p>
              </CardContent>
            </Card>
          ) : (
            filteredFollowUps.map((f) => (
              <Card key={f.id} className={cardBg}>
                <CardContent className="p-4">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold text-lg">
                          {f.lead_name?.charAt(0).toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`${textPrimary} font-medium`}>{f.lead_name}</p>
                          <SourceBadge source={f.lead_source} />
                          {getStatusBadge(f.status)}
                          {/* Lead Score Badge */}
                          {f.lead_score !== undefined && (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                              f.lead_score >= 80 ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                              f.lead_score >= 60 ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                              f.lead_score >= 40 ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' :
                              'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                            }`}>
                              {f.is_hot_lead && '🔥'}
                              {f.lead_score}pts ({f.score_grade || 'D'})
                            </span>
                          )}
                        </div>
                        <p className={`${textMuted} text-sm flex items-center gap-1 flex-wrap`}>
                          <Phone className="w-3 h-3" />
                          {f.lead_phone}
                          {f.lead_email && (
                            <>
                              <span className="mx-2">|</span>
                              <Mail className="w-3 h-3" />
                              {f.lead_email}
                            </>
                          )}
                          <span className="mx-2">|</span>
                          <Calendar className="w-3 h-3" />
                          Iniciado: {formatDateTime(f.created_at)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setExpandedLead(expandedLead === f.id ? null : f.id)}
                        className={textSecondary}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Detalles
                        {expandedLead === f.id ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
                      </Button>
                      {(() => {
                        const matchedLead = leadFromFollowUp(f);
                        if (!matchedLead) return null;
                        const has = matchedLead.outcome?.attended || matchedLead.outcome?.converted_to_surgery;
                        return (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openOutcome(matchedLead)}
                            className={`${theme === 'dark' ? 'border-emerald-600 text-emerald-400 hover:bg-emerald-600/10' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
                          >
                            <DollarSign className="w-4 h-4 mr-1" />
                            {has ? 'Editar ingreso' : 'Registrar ingreso'}
                          </Button>
                        );
                      })()}
                      {f.status !== 'responded' && f.status !== 'opted_out' && f.status !== 'no_response' && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => markResponded(f.id)}
                            className="text-green-500 hover:text-green-600"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Respondió
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => optOut(f.id)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Opt-out
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Outcome summary + edit panel for the underlying lead */}
                  {(() => {
                    const matchedLead = leadFromFollowUp(f);
                    if (!matchedLead) return null;
                    return (
                      <>
                        {(matchedLead.outcome?.attended || matchedLead.outcome?.converted_to_surgery) && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {matchedLead.outcome.attended && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                                🩺 Consulta {fmtMoney(matchedLead.outcome.consultation_revenue)}
                              </span>
                            )}
                            {matchedLead.outcome.converted_to_surgery && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-500/20 text-purple-400 border border-purple-500/30">
                                ⚕️ {surgeryLabel(matchedLead.outcome.surgery_type)} {fmtMoney(matchedLead.outcome.surgery_revenue)}
                              </span>
                            )}
                          </div>
                        )}
                        <OutcomePanel lead={matchedLead} />
                      </>
                    );
                  })()}

                  {/* Timeline Visual */}
                  <div className="mt-4 relative">
                    <div className={`absolute top-4 left-0 right-0 h-1 ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'} rounded`}></div>
                    <div className="relative flex justify-between">
                      {/* Day 0 */}
                      <div className="flex flex-col items-center z-10">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${f.day0_sent_at ? 'bg-green-500' : 'bg-gray-400'}`}>
                          {f.day0_sent_at ? <CheckCircle2 className="w-4 h-4 text-white" /> : <Clock className="w-4 h-4 text-white" />}
                        </div>
                        <span className={`text-xs mt-1 font-medium ${f.day0_sent_at ? 'text-green-500' : textMuted}`}>Day 0</span>
                        {f.day0_sent_at && (
                          <span className={`text-xs ${textMuted}`}>{getTimeAgo(f.day0_sent_at)}</span>
                        )}
                      </div>

                      {/* +24h Reminder */}
                      <div className="flex flex-col items-center z-10">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${f.day0_reminder_sent_at ? 'bg-green-500' : 'bg-gray-400'}`}>
                          {f.day0_reminder_sent_at ? <CheckCircle2 className="w-4 h-4 text-white" /> : <Clock className="w-4 h-4 text-white" />}
                        </div>
                        <span className={`text-xs mt-1 font-medium ${f.day0_reminder_sent_at ? 'text-green-500' : textMuted}`}>+24h</span>
                        {f.day0_reminder_sent_at && (
                          <span className={`text-xs ${textMuted}`}>{getTimeAgo(f.day0_reminder_sent_at)}</span>
                        )}
                      </div>

                      {/* Day 3 */}
                      <div className="flex flex-col items-center z-10">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${f.day3_sent_at ? 'bg-green-500' : 'bg-gray-400'}`}>
                          {f.day3_sent_at ? <CheckCircle2 className="w-4 h-4 text-white" /> : <Clock className="w-4 h-4 text-white" />}
                        </div>
                        <span className={`text-xs mt-1 font-medium ${f.day3_sent_at ? 'text-green-500' : textMuted}`}>Day 3</span>
                        {f.day3_sent_at && (
                          <span className={`text-xs ${textMuted}`}>{getTimeAgo(f.day3_sent_at)}</span>
                        )}
                      </div>

                      {/* Day 7 */}
                      <div className="flex flex-col items-center z-10">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${f.day7_sent_at ? 'bg-green-500' : 'bg-gray-400'}`}>
                          {f.day7_sent_at ? <CheckCircle2 className="w-4 h-4 text-white" /> : <Clock className="w-4 h-4 text-white" />}
                        </div>
                        <span className={`text-xs mt-1 font-medium ${f.day7_sent_at ? 'text-green-500' : textMuted}`}>Day 7</span>
                        {f.day7_sent_at && (
                          <span className={`text-xs ${textMuted}`}>{getTimeAgo(f.day7_sent_at)}</span>
                        )}
                      </div>

                      {/* Response/Conversion */}
                      <div className="flex flex-col items-center z-10">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${f.status === 'responded' ? 'bg-green-500' : f.status === 'no_response' ? 'bg-red-400' : 'bg-gray-400'}`}>
                          {f.status === 'responded' ? <CheckCircle className="w-4 h-4 text-white" /> : <AlertCircle className="w-4 h-4 text-white" />}
                        </div>
                        <span className={`text-xs mt-1 font-medium ${f.status === 'responded' ? 'text-green-500' : f.status === 'no_response' ? 'text-red-400' : textMuted}`}>
                          {f.status === 'responded' ? '¡Conversión!' : f.status === 'no_response' ? 'Fin' : 'Espera'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedLead === f.id && (
                    <div className={`mt-4 pt-4 border-t ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
                      <h4 className={`${textPrimary} font-medium mb-3`}>Historial de mensajes enviados</h4>
                      <div className="space-y-3">
                        {f.day0_sent_at && (
                          <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-green-500 text-xs font-medium">Day 0 - Mensaje inicial</span>
                              <span className={`${textMuted} text-xs`}>{formatDateTime(f.day0_sent_at)}</span>
                            </div>
                            <p className={`${textSecondary} text-sm whitespace-pre-line`}>
                              Hola {f.lead_name}, soy Gina, asistente del Dr. César Sánchez de Optall Vision... [Mensaje de bienvenida]
                            </p>
                          </div>
                        )}
                        {f.day0_reminder_sent_at && (
                          <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-orange-500 text-xs font-medium">+24h - Recordatorio</span>
                              <span className={`${textMuted} text-xs`}>{formatDateTime(f.day0_reminder_sent_at)}</span>
                            </div>
                            <p className={`${textSecondary} text-sm whitespace-pre-line`}>
                              {f.lead_name}, ¿quedó alguna duda? Los espacios para consultas de valoración gratuitas se agendan rápido esta semana...
                            </p>
                          </div>
                        )}
                        {f.day3_sent_at && (
                          <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-purple-500 text-xs font-medium">Day 3 - Seguimiento cálido</span>
                              <span className={`${textMuted} text-xs`}>{formatDateTime(f.day3_sent_at)}</span>
                            </div>
                            <p className={`${textSecondary} text-sm whitespace-pre-line`}>
                              Hola {f.lead_name}, te escribo del consultorio del Dr. César Sánchez... ¿Qué te detiene?
                            </p>
                          </div>
                        )}
                        {f.day7_sent_at && (
                          <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-gray-500 text-xs font-medium">Day 7 - Mensaje final</span>
                              <span className={`${textMuted} text-xs`}>{formatDateTime(f.day7_sent_at)}</span>
                            </div>
                            <p className={`${textSecondary} text-sm whitespace-pre-line`}>
                              {f.lead_name}, última actualización... El Dr. Sánchez tiene más de 15,000 cirugías exitosas...
                            </p>
                          </div>
                        )}
                        {f.day0_response && (
                          <div className={`p-3 rounded-lg bg-green-500/10 border border-green-500/30`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-green-500 text-xs font-medium">Respuesta del lead</span>
                              <span className={`${textMuted} text-xs`}>{f.last_response_at ? formatDateTime(f.last_response_at) : ''}</span>
                            </div>
                            <p className={`${textPrimary} text-sm`}>"{f.day0_response}"</p>
                          </div>
                        )}
                        {!f.day0_sent_at && (
                          <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-yellow-500/10' : 'bg-yellow-50'} border border-yellow-500/30`}>
                            <p className="text-yellow-600 text-sm flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              Mensaje pendiente de envío. Se enviará en el próximo horario laboral (Lun-Sáb 9AM-8PM).
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Leads Tab */}
      {activeTab === 'leads' && (
        <div className="space-y-3">
          {/* Selection info bar */}
          {selectionMode && selectedLeads.length > 0 && (
            <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-purple-500/20' : 'bg-purple-50'} border border-purple-500/30`}>
              <p className="text-purple-500 text-sm font-medium">
                {selectedLeads.length} lead(s) seleccionado(s)
              </p>
            </div>
          )}
          
          {filteredLeads.length === 0 ? (
            <Card className={cardBg}>
              <CardContent className="py-12 text-center">
                <Users className={`w-12 h-12 ${textMuted} mx-auto mb-4`} />
                <p className={textSecondary}>No hay leads aún</p>
                <p className={`${textMuted} text-sm mt-1`}>Los leads aparecerán aquí cuando lleguen mensajes por WhatsApp</p>
              </CardContent>
            </Card>
          ) : (
            filteredLeads.map((lead) => {
              const hasFollowUp = followUps.some(f => f.lead_phone?.includes(lead.phone?.slice(-10)));
              const isSelected = selectedLeads.includes(lead.id);
              return (
                <Card 
                  key={lead.id} 
                  className={`${cardBg} ${isSelected ? 'ring-2 ring-purple-500' : ''} ${selectionMode && !hasFollowUp ? 'cursor-pointer hover:ring-1 hover:ring-purple-400' : ''}`}
                  onClick={() => selectionMode && !hasFollowUp && toggleSelectLead(lead.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Checkbox for selection mode */}
                        {selectionMode && !hasFollowUp && (
                          <div 
                            className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                              isSelected 
                                ? 'bg-purple-500 border-purple-500' 
                                : theme === 'dark' ? 'border-slate-500' : 'border-gray-300'
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSelectLead(lead.id);
                            }}
                          >
                            {isSelected && <CheckCircle2 className="w-4 h-4 text-white" />}
                          </div>
                        )}
                        
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${hasFollowUp ? 'bg-gradient-to-br from-green-500 to-emerald-600' : 'bg-gradient-to-br from-blue-500 to-cyan-600'}`}>
                          <span className="text-white font-semibold">
                            {(lead.name || lead.phone).charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`${textPrimary} font-medium`}>{lead.name || 'Sin nombre'}</p>
                            <SourceBadge source={lead.source} />
                            <FunnelBadge funnel={lead.funnel} />
                            {hasFollowUp && (
                              <span className="text-xs text-green-500 bg-green-500/10 px-2 py-0.5 rounded-full">
                                Follow-up activo
                              </span>
                            )}
                          </div>
                          <p className={`${textMuted} text-sm flex items-center gap-1`}>
                            <Phone className="w-3 h-3" />
                            {lead.phone}
                            {lead.funnel?.next_appointment && (
                              <span className="ml-2 text-yellow-500">
                                | 📅 Próxima: {new Date(lead.funnel.next_appointment.appointment_date).toLocaleDateString('es-MX')}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {!selectionMode && (
                          <>
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openOutcome(lead);
                              }}
                              className={lead.outcome?.attended || lead.outcome?.converted_to_surgery
                                ? 'bg-emerald-700 hover:bg-emerald-800 text-white'
                                : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                            >
                              <DollarSign className="w-4 h-4 mr-1" />
                              {lead.outcome?.attended || lead.outcome?.converted_to_surgery ? 'Editar ingreso' : '+ Registrar ingreso'}
                            </Button>
                            {!hasFollowUp ? (
                              <Button
                                size="sm"
                                onClick={() => startFollowUp(lead)}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                              >
                                <Play className="w-4 h-4 mr-1" />
                                Iniciar Follow-up
                              </Button>
                            ) : (
                              <span className={`text-xs ${textMuted} flex items-center gap-1`}>
                                <CheckCircle className="w-3 h-3 text-green-500" />
                                En secuencia
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Outcome summary badges */}
                    {(lead.outcome?.attended || lead.outcome?.converted_to_surgery) && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {lead.outcome.attended && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                            🩺 Consulta {fmtMoney(lead.outcome.consultation_revenue)}
                          </span>
                        )}
                        {lead.outcome.converted_to_surgery && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-500/20 text-purple-400 border border-purple-500/30">
                            ⚕️ {surgeryLabel(lead.outcome.surgery_type)} {fmtMoney(lead.outcome.surgery_revenue)}
                          </span>
                        )}
                      </div>
                    )}

                    {lead.ai_summary && (
                      <div className={`mt-3 pt-3 border-t ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
                        <p className={`${textSecondary} text-sm`}>
                          <span className={textMuted}>Resumen IA:</span> {lead.ai_summary}
                        </p>
                      </div>
                    )}

                    <OutcomePanel lead={lead} />
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}
    </DashboardLayout>
  );
};

export default Leads;
